/**
 * @fileoverview Sort, search orchestration, export, and navigation handlers.
 * @module hooks/companyCatalog/useCompanyCatalogControls
 */
import { useCallback, useState } from "react";
import {
  fetchRemainingCompaniesFromDynamoDB,
  handleExportCompaniesCsv,
} from "../../logic/companyCatalog/index.js";
import { logger, sanitizeError } from "../../utils/logger.js";

const DEFAULT_SORT_KEY = "nombre";

/**
 * Owns toolbar interactions that do not mutate catalog data directly: sorting,
 * search submission, page-size changes, CSV export, and edit navigation.
 *
 * @param {Object} options Hook dependencies from the parent catalog composer
 * @param {import('react-router').NavigateFunction} options.navigate Router navigate for edit-company routes
 * @param {Array<Object>} options.companiesData Rows currently loaded in the catalog table
 * @param {() => Promise<{ resetPage: boolean }>} options.handleSearch RFC search handler from useCompanyCatalogSearch
 * @param {() => void} options.resetPagination Resets page index before a new search or page-size change
 * @param {import('react').Dispatch<import('react').SetStateAction<number>>} options.setResultsLimit Updates selected page size
 * @param {boolean} options.isRfcSearchActive True when the table is showing an RFC lookup result
 * @param {string|null} options.lastKey DynamoDB cursor for pages not yet loaded
 * @param {import('react').Dispatch<import('react').SetStateAction<boolean>>} options.setExportLoading Toggles the catalog loading state during a full export
 * @returns {{
 *   sortConfig: { key: string, direction: 'ascending'|'descending' },
 *   handleSort: () => void,
 *   runSearch: () => Promise<void>,
 *   handleResultsLimitChange: (selectedOption: { value: number }) => void,
 *   handleExportCsv: () => Promise<void>,
 *   handleEditCompanyClick: (event: import('react').MouseEvent<HTMLElement>) => void
 * }} Toolbar handlers exposed to the CompanyCatalog page
 */
export const useCompanyCatalogControls = ({
  navigate,
  companiesData,
  handleSearch,
  resetPagination,
  setResultsLimit,
  isRfcSearchActive,
  lastKey,
  setExportLoading,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: DEFAULT_SORT_KEY,
    direction: "ascending",
  });

  const handleSort = useCallback(() => {
    setSortConfig((prev) => ({
      key: DEFAULT_SORT_KEY,
      direction: prev.direction === "ascending" ? "descending" : "ascending",
    }));
  }, []);

  const runSearch = useCallback(async () => {
    resetPagination();
    await handleSearch();
  }, [handleSearch, resetPagination]);

  const handleResultsLimitChange = useCallback(
    (selectedOption) => {
      setResultsLimit(selectedOption.value);
      resetPagination();
    },
    [resetPagination, setResultsLimit],
  );

  const handleExportCsv = useCallback(async () => {
    if (isRfcSearchActive || !lastKey) {
      handleExportCompaniesCsv(companiesData);
      return;
    }

    setExportLoading(true);
    try {
      const remainingCompanies = await fetchRemainingCompaniesFromDynamoDB(
        lastKey,
        navigate,
      );
      handleExportCompaniesCsv([...companiesData, ...remainingCompanies]);
    } catch (error) {
      logger.error("Error exporting companies:", {
        err: sanitizeError(error),
      });
    } finally {
      setExportLoading(false);
    }
  }, [companiesData, isRfcSearchActive, lastKey, navigate, setExportLoading]);

  const handleEditCompanyClick = useCallback(
    (event) => {
      const companyId = event.currentTarget.getAttribute("data-company-id");
      if (!companyId) {
        return;
      }
      navigate(`/companyEdit/${companyId}`, {
        state: {
          from: "companyCatalog",
          context: "companyCatalog",
        },
      });
    },
    [navigate],
  );

  return {
    sortConfig,
    handleSort,
    runSearch,
    handleResultsLimitChange,
    handleExportCsv,
    handleEditCompanyClick,
  };
};
